import { useEffect, useRef, useState } from 'react';
import Badge from '@mui/material/Badge';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { FeedPanel } from './FeedPanel';

/**
 * The bell (plan Phase 3): the what-changed feed's button in the top bar.
 *
 * It renders inside `TopbarRight`, between the presence stack and the user chip, as a
 * NEVER-OVERFLOW fixture — deliberately not in the control registry, the same way presence
 * and the user chip are placed, so the fit controller can never move it into a "…" menu.
 * No new root, no new host: the whole feature stays out of `index.html` and `main.tsx`.
 *
 * THE BADGE REFRESHES ON EXACTLY FOUR EVENTS, never a timer: sign-in (`enterApp()` loads it
 * with the subscriptions — the region mounts before boot, so that IS the mount refresh),
 * window focus (the listener below — "I came back, what happened"), a doc save
 * (`afterDocSaved`), and a rename whose response says it moved the feed (`adoptNodeSlug`,
 * V0.45.1 — a rename can enqueue a `slug_stale` row, and with no fourth event the count sat
 * stale until a focus or a reload). Polling is the one delivery mechanism the plan rejected,
 * which is why each of the four is named here: a fifth has to earn its place the same way.
 *
 * The focus listener is registered once per signed-in session and gated on `booted`,
 * because a region's first mount runs before boot has established anything (HANDOFF §7) —
 * an ungated listener would fire a fetch against a session that does not exist yet.
 */
export function FeedBell() {
  const s = useAppState();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  const signedIn = s.booted && !!s.currentUser;

  useEffect(() => {
    if (!signedIn) return;
    const refresh = () => callAction('refreshFeedBadge');
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [signedIn]);

  if (!s.currentUser) return null;

  return (
    <>
      <button
        className="btn feedbell"
        id="btn-feed"
        title="What changed"
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : 'false'}
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
      >
        <Badge badgeContent={s.feedUnread} max={99} overlap="circular">
          <svg className="ico">
            <use href="#i-bell" />
          </svg>
        </Badge>
      </button>
      <FeedPanel open={open} anchorEl={btnRef.current} onClose={() => setOpen(false)} />
    </>
  );
}
